/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Radio from 'backbone.radio';

import PhoneFormatBehavior from '@/behaviors/PhoneFormatBehavior';
import lazyLoadView from '@/util/lazyLoadView';
import showModal from '@/util/showModal';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';

import loadPOCDetailsModalView from '../../../external-dashboard/loaders/POCDetailsModalViewLoader';
import updateExternalUserDetails from '../../../login/services/UpdateExternalUserDetails';
// import UpdateCommand from '../../services/UpdateCommand';

import tmplCustomerDetailsView from './CustomerDetailsView.hbs';

const CustomerDetailsView = View.extend({
  template: tmplCustomerDetailsView,

  className: 'command-details-view',

  ui: {
    saveButton: '#saveButton',
    editButton: '#editButton',
    formInputs: '.form-inputs',
    pointsOfContactGridContainer: '#pointsOfContactGridContainer',
    addPOC: '.add-poc'
  },

  regions: {
    pointsOfContactGridContainer: '@ui.pointsOfContactGridContainer'
  },

  events: {
    'click @ui.editButton': 'onClickEditCommand',
    'click @ui.saveButton': 'onClickSaveCommand',
    'click @ui.addPOC': 'addPOC'
  },

  behaviors: {
    telephone: PhoneFormatBehavior
  },

  onRender() {
    Syphon.deserialize(this, this.model.get('customerDetails'));
    this.ui.formInputs.find(':input').prop('disabled', true);
  },

  onClickEditCommand(e) {
    e.preventDefault();
    this.ui.editButton.hide();
    this.ui.formInputs.find(':input').prop('disabled', false);
    Radio.channel('sideNavSectionView').trigger('toggle:saveBtn', true);
  },

  async onSave() {
    const formData = Syphon.serialize(this.ui.formInputs);
    formData.customerId = this.model.get('customerDetails').id;
    formData.otdsUserId = this.model.get('customerDetails').otdsUserId;
    formData.isAuthorized = this.model.get('customerDetails').isAuthorized;
    formData.actingAsCommandId = this.model.get('customerDetails').actingAsCommandId;
    formData.isCACLogin = this.model.get('customerDetails').isCACLogin;

    try {
      await validateForm(this.ui.formInputs);
      this.ui.formInputs.find(':input').prop('disabled', true);
      this.ui.editButton.show();
      Radio.channel('sideNavSectionView').trigger('toggle:saveBtn', false);
      await updateExternalUserDetails(formData);
      this.showSaveSuccess();
      Radio.channel('customer').trigger('update:header', formData);
    } catch (err) {
      this.showSaveError();
    }
  },

  async addPOC(e) {
    e.preventDefault();
    try {
      const formData = await showModal({
        title: 'Add Point of Contact',
        dialogClasses: 'modal-lg',
        view: lazyLoadView(() => loadPOCDetailsModalView(this.model.get('commandId')))
      });
      if (formData) {
        formData.name = `${formData.firstName} ${formData.lastName}`;
        this.getChildView('pointsOfContactGridContainer')
          .getChildView('viewContainer')
          .options.collection.unshift(formData);
      }
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Command Saved',
      message: 'This command has been saved successfully.',
      hideAfter: 5
    });
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving the command. Try again or contact an administrator if your issue persists.'
    });
  }
});

export default CustomerDetailsView;
